import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Eye, EyeOff, Loader2, Mail, Shield } from 'lucide-react';
import { useGoogleAuth } from '../hooks/useGoogleAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const navigate = useNavigate();
  const { login, checkAuth } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Email PIN 2FA state
  const [showPinLogin, setShowPinLogin] = useState(false);
  const [pinEmail, setPinEmail] = useState('');
  const [emailPin, setEmailPin] = useState('');
  const [pinSent, setPinSent] = useState(false);
  const [pinLoading, setPinLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password, pin || undefined);
    
    if (result.success) {
      const userData = result.user || {};
      if (userData.role === 'admin') navigate('/admin');
      else if (userData.role === 'assistant') navigate('/assistant');
      else navigate('/dashboard');
    } else {
      if (result.error?.includes('PIN')) {
        setShowPin(true);
        setError('Ingrese su PIN de 4 digitos');
      } else {
        setError(result.error);
      }
    }
    
    setLoading(false);
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = useGoogleAuth({
    onSuccess: async (user) => {
      await checkAuth();
      if (user.role === 'admin') navigate('/admin', { replace: true });
      else if (user.role === 'assistant') navigate('/assistant', { replace: true });
      else navigate('/dashboard', { replace: true });
    },
    onError: (msg) => setError(msg),
  });

  const handleSendPin = async () => {
    if (!pinEmail) { setError('Ingresa tu email'); return; }
    setPinLoading(true);
    setError('');
    try {
      await axios.post(`${API_URL}/api/auth/send-pin`, { email: pinEmail }, { withCredentials: true });
      setPinSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error enviando PIN');
    }
    setPinLoading(false);
  };

  const handleVerifyPin = async () => {
    if (!emailPin || emailPin.length !== 6) { setError('Ingresa el PIN de 6 digitos'); return; }
    setPinLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/auth/verify-pin`, { email: pinEmail, pin: emailPin }, { withCredentials: true });
      await checkAuth();
      const user = res.data;
      if (user.role === 'admin') navigate('/admin');
      else if (user.role === 'assistant') navigate('/assistant');
      else navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'PIN incorrecto o expirado');
    }
    setPinLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 pb-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-block">
            <img 
              src={`${API_URL}/api/uploads/og_logo.png`} 
              alt="OG MUSIC GROUP" 
              className="h-16 mx-auto"
              data-testid="login-logo"
            />
          </Link>
        </div>
        <div className="glass-card p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold mb-2">{t('auth.welcomeBack')}</h1>
            <p className="text-white/50 text-sm">{t('auth.signIn')}</p>
          </div>

          {/* Google Auth Button */}
          <button onClick={handleGoogleLogin}
            className="w-full flex items-center justify-center gap-3 py-3.5 bg-white/[0.07] hover:bg-white/[0.12] border border-white/10 rounded-xl text-sm font-medium text-white transition-colors mb-4"
            data-testid="google-login-btn">
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continuar con Google
          </button>

          {/* Email PIN Login Button */}
          <button onClick={() => { setShowPinLogin(!showPinLogin); setPinSent(false); setError(''); }}
            className="w-full flex items-center justify-center gap-2 py-3 bg-white/[0.03] hover:bg-white/[0.07] border border-white/5 rounded-xl text-xs text-white/50 transition-colors mb-6"
            data-testid="pin-login-toggle">
            <Shield className="w-3.5 h-3.5" />
            {showPinLogin ? 'Volver a login con contrasena' : 'Verificacion por PIN (email)'}
          </button>

          {/* Email PIN Login Form */}
          {showPinLogin ? (
            <div className="space-y-4" data-testid="pin-login-form">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="login-error">
                  {error}
                </div>
              )}
              {!pinSent ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Tu email</label>
                    <input type="email" value={pinEmail} onChange={e => setPinEmail(e.target.value)}
                      className="w-full input-glass" placeholder="you@example.com" data-testid="pin-email-input" />
                  </div>
                  <button onClick={handleSendPin} disabled={pinLoading}
                    className="w-full btn-primary py-3.5 flex items-center justify-center gap-2" data-testid="send-pin-btn">
                    {pinLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Enviar PIN a mi email
                  </button>
                </>
              ) : (
                <>
                  <p className="text-sm text-green-400 text-center">PIN enviado a {pinEmail}</p>
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">Codigo PIN (6 digitos)</label>
                    <input type="text" inputMode="numeric" maxLength={6} value={emailPin}
                      onChange={e => setEmailPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="w-full input-glass text-center tracking-[0.5em] text-2xl py-4" placeholder="------"
                      autoFocus data-testid="verify-pin-input" />
                  </div>
                  <button onClick={handleVerifyPin} disabled={pinLoading}
                    className="w-full btn-primary py-3.5 flex items-center justify-center gap-2" data-testid="verify-pin-btn">
                    {pinLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                    Verificar PIN
                  </button>
                  <button onClick={() => { setPinSent(false); setEmailPin(''); }}
                    className="w-full text-xs text-white/40 hover:text-white/60 py-2">
                    Reenviar PIN
                  </button>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Divider */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-white/30">o con email y contrasena</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>

              {/* Regular Login Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {error && (
                  <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm" data-testid="login-error">
                    {error}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">{t('auth.email')}</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    className="w-full input-glass" placeholder="you@example.com" required data-testid="login-email" />
                </div>

                <div>
                  <label className="block text-sm font-medium text-white/70 mb-2">{t('auth.password')}</label>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                      className="w-full input-glass pr-12" placeholder="••••••••" required data-testid="login-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/60">
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-white/60">
                    <input type="checkbox" className="rounded border-white/20 bg-white/5" />
                    {t('auth.rememberMe')}
                  </label>
                  <Link to="/forgot-password" className="text-[#E8720C] hover:underline">
                    {t('auth.forgotPassword')}
                  </Link>
                </div>

                {showPin && (
                  <div>
                    <label className="block text-sm font-medium text-white/70 mb-2">PIN (4 digitos)</label>
                    <input type="password" inputMode="numeric" maxLength={4} value={pin}
                      onChange={e => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      className="w-full input-glass text-center tracking-[0.5em] text-xl" placeholder="****"
                      autoFocus data-testid="login-pin" />
                  </div>
                )}

                <button type="submit" disabled={loading}
                  className="w-full btn-primary py-4 flex items-center justify-center gap-2" data-testid="login-submit">
                  {loading ? <><Loader2 className="w-5 h-5 animate-spin" />{t('auth.signingIn')}</> : t('auth.signInBtn')}
                </button>
              </form>
            </>
          )}

          <p className="text-center text-sm text-white/50 mt-6">
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-[#E8720C] hover:underline">{t('auth.createOne')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
